/*
 * Advances the "As worn" row one photo at a time, endlessly, the way the review
 * carousel below it does.
 *
 * Dawn's slider-component has no auto-advance, so this scrolls the track on a timer
 * and lets the stylesheet's scroll-behavior do the easing - the same easing Dawn's own
 * arrows use, so a click and a tick feel identical.
 *
 * Looping: the section renders the photos twice, so once the row has travelled the
 * width of one set it is snapped back by exactly that much. The two halves are the
 * same pictures, so the jump lands on an identical frame and cannot be seen. Nothing
 * is moved in the DOM and the track can never run out of tiles - which is what left a
 * gap at the end when this recycled elements instead.
 *
 * Pausing is deliberately narrow: off screen, mid-swipe, on keyboard focus, and for
 * reduced motion. Hovering does not stop it unless the merchant asks for that, since
 * a row that freezes whenever the cursor crosses it reads as broken.
 */
(function () {
  var SECTION = '[data-ugc-auto-scroll]';
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var TOUCH_RESUME_MS = 1200; // let flick momentum finish before taking over again

  function start(section) {
    var track = section.querySelector('.slider');
    if (!track) return;

    // The theme editor re-renders a section on every settings change, which re-runs
    // this script tag. Without a guard each edit leaves another timer running and the
    // row jumps two, three, four photos at a time.
    if (section.dataset.ugcScrollBound === 'true') return;
    section.dataset.ugcScrollBound = 'true';

    // Dawn's SliderComponent greys out an arrow as soon as it believes the track has
    // reached an end. This row is an endless loop, so there is no end to reach - its
    // own looping flag makes update() bail before the disabling, leaving the counter
    // and everything else intact.
    function keepArrowsLive() {
      if ('enableSliderLooping' in section) section.enableSliderLooping = true;
      var buttons = section.querySelectorAll('.slider-button');
      Array.prototype.forEach.call(buttons, function (button) {
        button.removeAttribute('disabled');
      });
    }
    keepArrowsLive();
    // global.js also defers, so the element may not be upgraded yet on first run.
    setTimeout(keepArrowsLive, 0);

    var seconds = parseFloat(section.dataset.ugcAutoScroll) || 3;
    var loopSize = parseInt(section.dataset.ugcLoopSize, 10) || 0;
    var pauseOnHover = section.dataset.ugcPauseOnHover === 'true';
    var timer = null;
    var visible = false;
    var held = false;
    var resumeTimer = null;

    function stride() {
      var first = track.firstElementChild;
      if (!first) return 0;
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    // Jump a whole set, without easing - the frame is identical either way, so the
    // move is invisible only if it happens in one go.
    function shift(delta) {
      var previous = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = track.scrollLeft + delta;
      track.offsetHeight; // flush before easing is restored
      track.style.scrollBehavior = previous;
    }

    function wrap(setWidth) {
      shift(-setWidth);
    }

    // Dawn scrolls by reading scrollLeft and adding a tile, so at either end of the
    // track its target is clamped and the arrow does nothing. Catching the click on
    // the way down lets the position be moved a whole set first, giving Dawn room to
    // scroll into - which is why the arrows keep working on an endless row.
    function makeRoom(event) {
      var button = event.target.closest && event.target.closest('.slider-button');
      if (!button) return;

      var width = stride();
      var setWidth = loopSize * width;
      if (!width || !setWidth) return;

      if (button.name === 'next') {
        var furthest = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft + width > furthest + 2) shift(-setWidth);
      } else if (track.scrollLeft - width < -2) {
        shift(setWidth);
      }

      // Don't let the timer fire on top of a deliberate click.
      hold();
      release(seconds * 1000);
    }

    function step() {
      var width = stride();
      if (!width) return;

      // Wrap on reaching one set, and as a backstop if the next move would run past
      // the end of the track - so a mis-measured set can stall the row at worst for
      // one tick rather than for good.
      var setWidth = loopSize * width;
      var furthest = track.scrollWidth - track.clientWidth;
      if (setWidth && (track.scrollLeft >= setWidth - 2 || track.scrollLeft + width > furthest + 2)) {
        wrap(setWidth);
      }

      track.scrollTo({ left: track.scrollLeft + width, behavior: 'smooth' });
      keepArrowsLive();
    }

    function run() {
      if (timer !== null || !visible || held || REDUCED.matches) return;
      timer = setInterval(step, seconds * 1000);
    }

    function halt() {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    }

    function hold() {
      clearTimeout(resumeTimer);
      held = true;
      halt();
    }

    function release(delay) {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        held = false;
        run();
      }, delay || 0);
    }

    section.addEventListener('click', makeRoom, true);

    if (pauseOnHover) {
      section.addEventListener('mouseenter', hold);
      section.addEventListener('mouseleave', function () {
        release();
      });
    }

    section.addEventListener('focusin', hold);
    section.addEventListener('focusout', function () {
      release();
    });
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', function () {
      release(TOUCH_RESUME_MS);
    }, { passive: true });
    REDUCED.addEventListener('change', function () {
      REDUCED.matches ? halt() : run();
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          visible ? run() : halt();
        },
        { threshold: 0.1 }
      ).observe(section);
    } else {
      visible = true;
      run();
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SECTION), start);
    if (scope !== document && scope.matches && scope.matches(SECTION)) start(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Re-bind only what the editor has just replaced; the guard above keeps the rest
  // from picking up a second timer.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
